<template>
  <div class="form-validation">
    <!-- Slot para contenido del formulario -->
    <slot 
      :errors="errors"
      :isValid="isValid"
      :validate="validate"
      :validateField="validateField"
      :clearErrors="clearErrors"
      :setFieldValue="setFieldValue"
      :getFieldValue="getFieldValue"
      :hasError="hasError"
    />
    
    <!-- Indicador de estado de validación -->
    <div v-if="showValidationSummary" class="validation-summary mt-4">
      <v-alert 
        v-if="!isValid && Object.keys(errors).length > 0"
        type="error" 
        variant="tonal"
        density="compact"
      >
        <div class="text-subtitle-2 mb-2">Por favor corrige los siguientes errores:</div>
        <ul class="ma-0 pa-0">
          <li v-for="(error, field) in errors" :key="field" class="text-caption">
            {{ error }}
          </li>
        </ul>
      </v-alert>
      
      <v-alert 
        v-else-if="isValid && Object.keys(formData).length > 0"
        type="success" 
        variant="tonal"
        density="compact"
      >
        ✅ Formulario válido y listo para enviar
      </v-alert>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { FormValidationService } from '@/services/formApiService'

interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  email?: boolean
  phone?: boolean
  number?: boolean
  min?: number
  max?: number
  custom?: (value: any) => string | boolean
  asyncValidation?: 'email-unique' | 'username-unique' | string
  message?: string
}

interface FormValidationProps {
  modelValue?: Record<string, any>
  rules?: Record<string, ValidationRule[]>
  showValidationSummary?: boolean
  validateOnChange?: boolean
  validateOnBlur?: boolean
  debounceTime?: number
  enableAsyncValidation?: boolean
  excludeFromAsyncValidation?: Record<string, any>
}

interface FormValidationEmits {
  (e: 'update:modelValue', value: Record<string, any>): void
  (e: 'validation-change', isValid: boolean, errors: Record<string, string>): void
  (e: 'field-change', field: string, value: any): void
}

const props = withDefaults(defineProps<FormValidationProps>(), {
  modelValue: () => ({}),
  rules: () => ({}),
  showValidationSummary: true,
  validateOnChange: true,
  validateOnBlur: true,
  debounceTime: 300,
  enableAsyncValidation: true,
  excludeFromAsyncValidation: () => ({})
})

const emit = defineEmits<FormValidationEmits>()

// Estado reactivo
const formData = ref<Record<string, any>>({ ...props.modelValue })
const errors = ref<Record<string, string>>({})
const touched = ref<Record<string, boolean>>({})
const validationTimeout = ref<NodeJS.Timeout>()
const lastChangedField = ref<string | null>(null)
const asyncValidationInProgress = ref<Record<string, boolean>>({})

// Computed
const isValid = computed(() => {
  const hasErrors = Object.keys(errors.value).length === 0
  const hasData = Object.keys(formData.value).length > 0
  const noAsyncValidationInProgress = Object.keys(asyncValidationInProgress.value).length === 0
  return hasErrors && hasData && noAsyncValidationInProgress
})

// Watchers
watch(() => props.modelValue, (newValue) => {
  formData.value = { ...newValue }
}, { deep: true })

watch(formData, (newValue) => {
  emit('update:modelValue', newValue)
  if (props.validateOnChange) {
    debouncedValidate()
  }
}, { deep: true })

watch([isValid, errors], ([valid, errs]) => {
  emit('validation-change', valid, errs)
}, { deep: true })

onMounted(() => {
  emit('validation-change', isValid.value, errors.value)
})

// Funciones de validación
const validateField = async (field: string, value: any): Promise<string> => {
  const fieldRules = props.rules[field] || []
  
  for (const rule of fieldRules) {
    // Required
    if (rule.required && (!value || value.toString().trim() === '')) {
      return rule.message || `El campo ${field} es requerido`
    }
    
    // Skip other validations if value is empty and not required
    if (!value || value.toString().trim() === '') continue
    
    // String validations
    if (typeof value === 'string') {
      // MinLength
      if (rule.minLength && value.length < rule.minLength) {
        return rule.message || `Mínimo ${rule.minLength} caracteres requeridos`
      }
      
      // MaxLength
      if (rule.maxLength && value.length > rule.maxLength) {
        return rule.message || `Máximo ${rule.maxLength} caracteres permitidos`
      }
      
      // Email
      if (rule.email) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailPattern.test(value)) {
          return rule.message || 'Formato de email inválido'
        }
      }
      
      // Phone
      if (rule.phone) {
        const phonePattern = /^[\+]?[\d\s\-\(\)]{10,}$/
        if (!phonePattern.test(value)) {
          return rule.message || 'Formato de teléfono inválido'
        }
      }
      
      // Pattern
      if (rule.pattern && !rule.pattern.test(value)) {
        return rule.message || 'Formato inválido'
      }
    }
    
    // Number validations
    if (rule.number || typeof value === 'number') {
      const numValue = typeof value === 'number' ? value : parseFloat(value)
      
      if (rule.number && isNaN(numValue)) {
        return rule.message || 'Debe ser un número válido'
      }
      
      if (rule.min !== undefined && numValue < rule.min) {
        return rule.message || `Valor mínimo: ${rule.min}`
      }
      
      if (rule.max !== undefined && numValue > rule.max) {
        return rule.message || `Valor máximo: ${rule.max}`
      }
    }
    
    // Custom validation
    if (rule.custom) {
      const customResult = rule.custom(value)
      if (typeof customResult === 'string') {
        return customResult
      }
      if (customResult === false) {
        return rule.message || 'Validación personalizada falló'
      }
    }
    
    // Async validation
    if (rule.asyncValidation && props.enableAsyncValidation) {
      const asyncError = await performAsyncValidation(field, value, rule.asyncValidation)
      if (asyncError) {
        return asyncError
      }
    }
  }
  
  return ''
}

const performAsyncValidation = async (field: string, value: any, validationType: string): Promise<string> => {
  if (!value || value.toString().trim() === '') return ''
  
  // Set loading state
  asyncValidationInProgress.value[field] = true
  
  try {
    switch (validationType) {
      case 'email-unique':
        const emailResult = await FormValidationService.validateEmailUniqueness(
          value, 
          props.excludeFromAsyncValidation?.userId
        )
        if (!emailResult.valid) {
          return emailResult.errors.email || 'Este email ya está en uso'
        }
        break
        
      case 'username-unique':
        const usernameResult = await FormValidationService.validateUsernameUniqueness(
          value,
          props.excludeFromAsyncValidation?.userId
        )
        if (!usernameResult.valid) {
          return usernameResult.errors.username || 'Este nombre de usuario ya está en uso'
        }
        break
        
      default:
        console.warn(`Unknown async validation type: ${validationType}`)
    }
    
    return ''
  } catch (error) {
    console.error(`Async validation error for ${field}:`, error)
    return 'Error al validar, intenta de nuevo'
  } finally {
    // Remove loading state
    delete asyncValidationInProgress.value[field]
  }
}

const validate = async (): Promise<boolean> => {
  const newErrors: Record<string, string> = {}
  
  for (const [field, value] of Object.entries(formData.value)) {
    const error = await validateField(field, value)
    if (error) {
      newErrors[field] = error
    }
  }
  
  // Validate required fields that might not be in formData
  for (const field of Object.keys(props.rules)) {
    if (!formData.value.hasOwnProperty(field)) {
      const error = await validateField(field, undefined)
      if (error) {
        newErrors[field] = error
      }
    }
  }
  
  errors.value = newErrors
  return Object.keys(newErrors).length === 0
}

const debouncedValidate = () => {
  if (validationTimeout.value) {
    clearTimeout(validationTimeout.value)
  }
  
  validationTimeout.value = setTimeout(async () => {
    // Valida solo el último campo cambiado para evitar churn innecesario
    const field = lastChangedField.value
    if (field) {
      const err = await validateField(field, formData.value[field])
      if (err) {
        errors.value[field] = err
      } else {
        delete errors.value[field]
      }
    } else {
      await validate()
    }
  }, props.debounceTime)
}

// Utility functions
const setFieldValue = (field: string, value: any) => {
  formData.value[field] = value
  touched.value[field] = true
  lastChangedField.value = field
  
  // Clear previous error for this field
  if (errors.value[field]) {
    delete errors.value[field]
  }
  
  emit('field-change', field, value)
}

const getFieldValue = (field: string) => {
  return formData.value[field]
}

const hasError = (field: string): boolean => {
  return !!errors.value[field]
}

const clearErrors = (fields?: string[]) => {
  if (fields) {
    fields.forEach(field => {
      delete errors.value[field]
    })
  } else {
    errors.value = {}
  }
}

const markFieldAsTouched = (field: string) => {
  touched.value[field] = true
  
  if (props.validateOnBlur) {
    nextTick(async () => {
      const error = await validateField(field, formData.value[field])
      if (error) {
        errors.value[field] = error
      } else {
        delete errors.value[field]
      }
    })
  }
}

// Expose methods for parent components
defineExpose({
  validate,
  validateField,
  clearErrors,
  setFieldValue,
  getFieldValue,
  hasError,
  isValid,
  errors,
  formData,
  markFieldAsTouched
})
</script>

<style scoped>
.form-validation {
  width: 100%;
}

.validation-summary {
  border-radius: 4px;
}

.validation-summary ul {
  list-style-type: none;
}

.validation-summary li::before {
  content: "• ";
  color: rgb(var(--v-theme-error));
  margin-right: 8px;
}
</style>
